import { useCallback, useEffect, useRef, useState } from "react";
import RecordButton from "./components/RecordButton";
import DiscardButton from "./components/DiscardButton";
import SendButton from "./components/SendButton";
import AudioWaves from "./components/AudioWaves";
import { useSearchParams } from "react-router-dom";
import PauseButton from "./components/PauseButon";

interface frameCommunicationSendData {
  uniqueLoadId?: string;
  action?: string;
  message?: string;
}

const RESET_DELAY_MS = 2500;

function AudioRecorder() {
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [audio, setAudio] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorSending, setErrorSending] = useState(false);
  const [fileTooBig, setFileTooBig] = useState(false);
  const [searchParams] = useSearchParams();
  const language = searchParams.get("LANG") ?? "en";

  const recorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioUrlRef = useRef("");

  recorderRef.current = recorder;
  mediaStreamRef.current = mediaStream;
  audioUrlRef.current = audioUrl;

  // O iframe do placement é reaproveitado entre aberturas, então o widget volta ao
  // estado inicial por conta própria, sem depender do fechamento da janela.
  const resetWidget = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }

    setRecorder(null);
    setMediaStream(null);
    setRecordingStatus("inactive");
    setAudio(null);
    setAudioUrl("");
    setSending(false);
    setSent(false);
    setErrorSending(false);
    setFileTooBig(false);
  }, []);

  useEffect(() => {
    if (!sent && !errorSending) return;

    const timeoutId = window.setTimeout(resetWidget, RESET_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [sent, errorSending, resetWidget]);

  // Reabrir o widget não remonta o React; sem isto a tela final anterior reaparece.
  useEffect(() => {
    const resetIfFinished = () => {
      if (document.visibilityState !== "visible") return;
      if (!sent && !errorSending) return;
      resetWidget();
    };

    document.addEventListener("visibilitychange", resetIfFinished);
    window.addEventListener("pageshow", resetIfFinished);
    window.addEventListener("focus", resetIfFinished);

    return () => {
      document.removeEventListener("visibilitychange", resetIfFinished);
      window.removeEventListener("pageshow", resetIfFinished);
      window.removeEventListener("focus", resetIfFinished);
    };
  }, [sent, errorSending, resetWidget]);

  useEffect(() => {
    if (!window.frameCommunication) {
      window.frameCommunication = { timeout: {} };
    }

    if (typeof window.postMessage !== "function") return;

    const onMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.action == "init") {
          window.frameCommunication.uniqueLoadId = data.uniqueLoadId;
          window.frameCommunication.postMessageSource = event.source;
          window.frameCommunication.postMessageOrigin =
            event.origin as WindowPostMessageOptions;
        }
      } catch (e) {
        return false;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const frameCommunicationSend = useCallback(
    (data: frameCommunicationSendData) => {
      data["uniqueLoadId"] = window.frameCommunication?.uniqueLoadId;

      const encodedData = JSON.stringify(data);

      if (typeof window.postMessage === "function") {
        if (window.frameCommunication?.postMessageSource) {
          window.frameCommunication.postMessageSource.postMessage(
            encodedData,
            window.frameCommunication.postMessageOrigin,
          );
        }
      }
    },
    [],
  );

  if (errorSending) {
    return (
      <div className="h-40 flex justify-center items-center bg-white space-x-4 text-red-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-20 h-20"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <div className="text-red-600 font-sans text-lg font-bold mb-3">
          {fileTooBig
            ? language === "br"
              ? "O tamanho do arquivo é maior que o suportado"
              : language === "la"
                ? "El tamaño del archivo es mayor que el admitido"
                : "File size is too big"
            : language === "br"
              ? "Erro ao enviar, por favor contate o suporte"
              : language === "la"
                ? "Error al enviar, por favor contacte con soporte"
                : "Error sending file, please contact our support"}
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="h-40 flex justify-center items-center bg-white space-x-4 text-green-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-16 h-16"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="text-green-600 font-sans text-lg font-bold mb-3 text-center">
          {language === "br"
            ? "Áudio enviado com sucesso"
            : language === "la"
              ? "Audio enviado con éxito"
              : "Audio sent successfully"}
        </div>
      </div>
    );
  }

  if (sending) {
    return (
      <div className="h-40 flex justify-center items-center bg-white space-x-4">
        <div className="text-primary-pure font-sans text-4xl font-bold mb-3">
          {language === "br" || language === "la" ? "Enviando" : "Sending"}
        </div>
        <div className="w-4 h-4 rounded-full animate-pulse bg-primary-pure"></div>
        <div className="w-4 h-4 rounded-full animate-pulse bg-primary-pure"></div>
        <div className="w-4 h-4 rounded-full animate-pulse bg-primary-pure"></div>
      </div>
    );
  }

  return (
    <div className="h-[150px] flex justify-center items-center bg-white ">
      {audio || recordingStatus === "recording" ? (
        <DiscardButton
          setAudio={setAudio}
          setAudioUrl={setAudioUrl}
          setMediaStream={setMediaStream}
          setRecordingStatus={setRecordingStatus}
          recorder={recorder}
          mediaStream={mediaStream}
        />
      ) : null}
      {recordingStatus === "recording" ? (
        <>
          <div className="mx-1 animate-record-pulse bg-red-600 block w-3 h-3 rounded-full"></div>
          <AudioWaves mediaStream={mediaStream} />
        </>
      ) : null}
      {audio && recordingStatus !== "recording" ? (
        <audio
          src={audioUrl}
          controls
          className="block w-[200px] mx-1"
          title="audio.mp3"
        ></audio>
      ) : null}
      {recordingStatus === "inactive" ? (
        <RecordButton
          setMediaStream={setMediaStream}
          setRecorder={setRecorder}
          recordingStatus={recordingStatus}
          setRecordingStatus={setRecordingStatus}
        />
      ) : null}
      {recordingStatus === "recording" ? (
        <PauseButton
          recorder={recorder}
          setAudio={setAudio}
          setAudioUrl={setAudioUrl}
          setRecordingStatus={setRecordingStatus}
        />
      ) : null}

      {audio ? (
        <SendButton
          audio={audio}
          frameCommunicationSend={frameCommunicationSend}
          setSending={setSending}
          setErrorSending={setErrorSending}
          setFileTooBig={setFileTooBig}
          setSent={setSent}
        />
      ) : null}
    </div>
  );
}

export default AudioRecorder;
